import React, { useState, useRef, useEffect } from "react";
import "./passport.css";
import { API_BASE_URL } from "../../config/api";

const REG_NO_PATTERN = /^\d{4}\/\d{6}$/;

function PassportUpload() {
  const [regNo, setRegNo] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [studentName, setStudentName] = useState("");
  const inputRef = useRef();

  useEffect(() => {
    if (!REG_NO_PATTERN.test(regNo)) return;
    setVerifying(true);
    setVerified(false);
    setStudentName("");
    setStatus({ type: "", text: "" });
    fetch(`${API_BASE_URL}/api/passport/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reg_no: regNo.trim() }),
    })
      .then((res) => res.json().then((json) => ({ ok: res.ok, json })))
      .then(({ ok, json }) => {
        if (!ok) setStatus({ type: "error", text: "Registration number not found." });
        else { setVerified(true); setStudentName(json.fullname || ""); }
      })
      .catch(() => setStatus({ type: "error", text: "Network error. Please try again." }))
      .finally(() => setVerifying(false));
  }, [regNo]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    setFile(selected);
    setPreview(URL.createObjectURL(selected));
    setStatus({ type: "", text: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!regNo.trim()) return setStatus({ type: "error", text: "Please enter your registration number." });
    if (!file) return setStatus({ type: "error", text: "Please select a passport photo." });

    setLoading(true);
    setStatus({ type: "", text: "" });

    try {
      const formData = new FormData();
      formData.append("reg_no", regNo.trim());
      formData.append("passport", file);

      const res = await fetch(`${API_BASE_URL}/api/passport/upload`, {
        method: "POST",
        body: formData,
      });
      const json = await res.json();

      if (!res.ok) {
        setStatus({ type: "error", text: json.message || "Upload failed." });
      } else {
        setStatus({ type: "success", text: "Passport uploaded successfully!" });
        setDone(true);
      }
    } catch {
      setStatus({ type: "error", text: "Network error. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pp_page">
      <div className="pp_card">
        <div className="pp_logo_row">
          <div className="pp_logo_circle">PS</div>
          <div>
            <p className="pp_faculty">Faculty of Pharmaceutical Sciences</p>
            <p className="pp_uni">University of Nigeria, Nsukka</p>
          </div>
        </div>

        <h2 className="pp_title">Passport Photo Upload</h2>
        <p className="pp_subtitle">
          Upload your passport photograph. You can only do this once — contact the admin for any changes.
        </p>

        {done ? (
          <div className="pp_success_box">
            {preview && <img src={preview} alt="Uploaded passport" className="pp_uploaded_img" />}
            <p className="pp_success_msg">✓ Your passport has been uploaded successfully.</p>
          </div>
        ) : (
          <form className="pp_form" onSubmit={handleSubmit}>
            <div className="pp_field">
              <label>Registration Number</label>
              <input
                type="text"
                placeholder="e.g. 2019/234567"
                value={regNo}
                onChange={(e) => { setRegNo(e.target.value); setVerified(false); setStudentName(""); setStatus({ type: "", text: "" }); }}
                disabled={loading}
              />
              {verifying && <p className="pp_verifying">Verifying...</p>}
              {verified && <p className="pp_verified">✓ {studentName}</p>}
            </div>

            {verified && (
            <div className="pp_field">
              <label>Passport Photo</label>
              <div
                className="pp_drop_zone"
                onClick={() => inputRef.current.click()}
              >
                {preview ? (
                  <img src={preview} alt="Preview" className="pp_preview" />
                ) : (
                  <div className="pp_drop_hint">
                    <span className="pp_drop_icon">📷</span>
                    <span>Click to select a photo</span>
                    <span className="pp_drop_sub">JPG, PNG — max 5MB</span>
                  </div>
                )}
              </div>
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
                disabled={loading}
              />
              {preview && (
                <button
                  type="button"
                  className="pp_change_btn"
                  onClick={() => inputRef.current.click()}
                  disabled={loading}
                >
                  Change photo
                </button>
              )}
            </div>
            )}

            {status.text && (
              <div className={`pp_status ${status.type}`}>{status.text}</div>
            )}

            <button className="pp_submit" type="submit" disabled={loading || !verified}>
              {loading ? "Uploading..." : "Upload Passport"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default PassportUpload;
